// Pure rules for stage-based ("staged") tournaments: stage defaults, presets,
// validation, leaderboard ranking, advancement between stages and the
// lifecycle guards the admin UI enforces. No Firebase or React in here.

import { MAX_BRACKET_PLAYERS, buildBracket, resolveBracket } from './bracket';

export const STAGED_FORMAT = 'staged';
export const DEFAULT_POINTS_TABLE = [10, 8, 6, 5, 4, 3, 2, 1];

// Category ids are used as Firestore field-path segments, so keep them
// alphanumeric.
let categorySeq = 0;
export const newCategoryId = () => {
  categorySeq += 1;
  return `c${Date.now().toString(36)}${categorySeq}`;
};

// A category whose value is the average community rating (1-10) instead of
// an admin-entered number. Only on points leaderboards.
export const newVoteCategory = () => ({ id: newCategoryId(), name: 'Community', source: 'votes' });

export const isVoteCategory = (category) => category?.source === 'votes';

export const hasVoteCategory = (stage) =>
  stage?.type === 'leaderboard' && (stage.categories || []).some(isVoteCategory);

export const newStage = (overrides = {}) => ({
  name: 'Stage',
  type: 'leaderboard',
  status: 'pending',
  advanceCount: null,
  categories: [{ id: newCategoryId(), name: 'Points' }],
  elimination: 'double',
  participants: [],
  results: {},
  ...overrides,
});

export const STAGE_PRESETS = [
  {
    id: 'leaderboard',
    label: 'Leaderboard',
    description: 'One ranking. First place wins the tournament.',
    build: () => [newStage({ name: 'Leaderboard' })],
  },
  {
    id: 'qualifier-double',
    label: 'Qualifier + double elimination',
    description: 'Leaderboard qualifier. The top 8 play a double-elimination bracket.',
    build: () => [
      newStage({ name: 'Qualifier' }),
      newStage({
        name: 'Playoffs',
        type: 'bracket',
        advanceCount: 8,
        elimination: 'double',
      }),
    ],
  },
  {
    id: 'qualifier-single',
    label: 'Qualifier + single elimination',
    description: 'Leaderboard qualifier. The top 8 play a single-elimination bracket.',
    build: () => [
      newStage({ name: 'Qualifier' }),
      newStage({
        name: 'Playoffs',
        type: 'bracket',
        advanceCount: 8,
        elimination: 'single',
      }),
    ],
  },
  {
    id: 'bracket',
    label: 'Bracket only',
    description: 'A seeded double-elimination bracket with every player.',
    build: () => [newStage({ name: 'Bracket', type: 'bracket' })],
  },
  {
    id: 'judged',
    label: 'Judged contest',
    description:
      'Judges score every entry per category, plus a community vote. Highest total wins.',
    build: () => [
      newStage({
        name: 'Judging',
        categories: [
          ...['Gameplay', 'Visuals', 'Theme', 'Fun'].map((name) => ({
            id: newCategoryId(),
            name,
          })),
          newVoteCategory(),
        ],
      }),
    ],
  },
];

export const STATUS_LABELS = {
  pending: 'Upcoming',
  active: 'Live',
  completed: 'Completed',
};

export const stageTypeLabel = (stage) => {
  if (stage.type !== 'bracket') return 'Leaderboard';
  return stage.elimination === 'double' ? 'Double elimination' : 'Single elimination';
};

// Label of the public competition tab: the stage type for one-stage events.
export const competitionLabel = (stages) => {
  if (stages.length !== 1) return 'Stages';
  return stages[0].type === 'bracket' ? 'Bracket' : 'Leaderboard';
};

// The settings an admin can edit, trimmed and typed for Firestore.
export const stageConfig = (stage) => ({
  name: (stage.name || '').trim(),
  type: stage.type,
  advanceCount: Number.isInteger(stage.advanceCount) ? stage.advanceCount : null,
  categories: (stage.categories || []).map((c) => ({
    id: c.id,
    name: (c.name || '').trim(),
    ...(isVoteCategory(c) ? { source: 'votes' } : {}),
  })),
  elimination: stage.elimination,
});

export const minParticipants = (stage) => {
  if (stage.type !== 'bracket') return 1;
  return stage.elimination === 'double' ? 3 : 2;
};

// Human-readable problems with one stage's settings ([] = valid). `prev` is
// the stage before it (undefined for the first stage).
export const validateStage = (stage, prev) => {
  const errors = [];
  const name = (stage.name || '').trim();
  if (!name) errors.push('Give the stage a name.');
  if (name.length > 60) errors.push('Stage names can be at most 60 characters.');
  if (!['leaderboard', 'bracket'].includes(stage.type)) errors.push('Pick a stage type.');

  if (prev) {
    const n = stage.advanceCount;
    const max = stage.type === 'bracket' ? MAX_BRACKET_PLAYERS : 1000;
    if (!Number.isInteger(n) || n < minParticipants(stage) || n > max) {
      errors.push(
        `Advancing players must be a whole number from ${minParticipants(stage)} to ${max}.`
      );
    } else if (Number.isInteger(prev.advanceCount) && n > prev.advanceCount) {
      errors.push(
        `Only ${prev.advanceCount} players reach ${prev.name}, so at most ${prev.advanceCount} can advance.`
      );
    }
  }

  if (stage.type === 'leaderboard') {
    const cats = stage.categories || [];
    if (cats.length < 1 || cats.length > 20) errors.push('Add between 1 and 20 categories.');
    if (cats.some((c) => !(c.name || '').trim() || c.name.trim().length > 40)) {
      errors.push('Every category needs a name (up to 40 characters).');
    }
    const voteCats = cats.filter(isVoteCategory).length;
    if (voteCats > 1) errors.push('A stage can have only one community vote.');
  }
  if (stage.type === 'bracket') {
    if (!['single', 'double'].includes(stage.elimination))
      errors.push('Pick single or double elimination.');
  }
  return errors;
};

// ---- Points -------------------------------------------------------------

export const parsePoints = (text) => {
  const t = String(text ?? '')
    .trim()
    .replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const formatPoints = (n) =>
  Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';

// Points behind the leader: 12 -> "−12". Nothing for the leader.
export const formatGap = (points) => (points > 0 ? `−${formatPoints(points)}` : '');

// ---- Ranking ------------------------------------------------------------

// Standard competition ranking (1, 2, 2, 4) over rows already sorted best
// first; `same(a, b)` says whether two neighbours tie.
const assignRanks = (rows, same) => {
  const out = [];
  rows.forEach((row, i) => {
    out.push({ ...row, rank: i > 0 && same(rows[i - 1], row) ? out[i - 1].rank : i + 1 });
  });
  return out;
};

// Ranked rows for a leaderboard stage: points, higher wins, categories add
// up. Players with no points entered at all come last with rank null.
export const rankLeaderboard = (stage) => {
  const { categories = [], participants = [], results = {} } = stage;

  const placements = {};
  categories.forEach((c) => {
    const entries = participants
      .map((p) => [p, results[p]?.[c.id]])
      .filter(([, v]) => Number.isFinite(v))
      .sort((x, y) => y[1] - x[1]);
    const ranks = {};
    entries.forEach(([p, v], i) => {
      ranks[p] = i > 0 && v === entries[i - 1][1] ? ranks[entries[i - 1][0]] : i + 1;
    });
    placements[c.id] = ranks;
  });

  const rows = participants.map((playerId) => {
    const values = {};
    const places = {};
    categories.forEach((c) => {
      const v = results[playerId]?.[c.id];
      values[c.id] = Number.isFinite(v) ? v : null;
      places[c.id] = placements[c.id][playerId] ?? null;
    });
    const done = categories.filter((c) => values[c.id] !== null).length;
    return {
      playerId,
      values,
      placements: places,
      done,
      complete: done === categories.length,
      total: categories.reduce((sum, c) => sum + (values[c.id] ?? 0), 0),
    };
  });

  const compare = (a, b) => b.total - a.total;
  const ranked = rows.filter((r) => r.done > 0).sort(compare);
  const withRanks = assignRanks(ranked, (a, b) => compare(a, b) === 0);
  const leader = withRanks[0];
  withRanks.forEach((r) => {
    r.gap = leader.total - r.total;
  });
  const unranked = rows.filter((r) => r.done === 0).map((r) => ({ ...r, rank: null, gap: null }));
  return [...withRanks, ...unranked];
};

// Grand final reset is switched off for now: the grand final alone decides.
export const bracketFor = (stage, count = (stage.participants || []).length) =>
  buildBracket(count, { ...stage, grandFinalReset: false });

// [{ playerId, rank }] best first. Brackets rank by knock-out round.
export const stageStandings = (stage) => {
  if (stage.status === 'completed' && Array.isArray(stage.standings)) return stage.standings;
  if (stage.type === 'bracket') {
    const participants = stage.participants || [];
    if (participants.length < 2) return [];
    const { placements, complete } = resolveBracket(
      bracketFor(stage),
      participants,
      stage.results || {}
    );
    // Without a champion the placements are partial (the first player knocked
    // out would read as #1), so an unfinished bracket has no standings yet.
    if (!complete) return [];
    return Object.entries(placements)
      .map(([playerId, rank]) => ({ playerId, rank }))
      .sort(
        (a, b) =>
          a.rank - b.rank || participants.indexOf(a.playerId) - participants.indexOf(b.playerId)
      );
  }
  return rankLeaderboard(stage)
    .filter((r) => r.rank !== null)
    .map(({ playerId, rank }) => ({ playerId, rank }));
};

// Who makes the top `count`. If the cut line splits a tie, `tied` lists the
// tied players and `needed` how many of them still get through.
export const advancementCut = (standings, count) => {
  if (count >= standings.length) {
    return { advancing: standings.map((r) => r.playerId), tied: [], needed: 0 };
  }
  const cutRank = standings[count - 1].rank;
  if (standings[count].rank !== cutRank) {
    return {
      advancing: standings.slice(0, count).map((r) => r.playerId),
      tied: [],
      needed: 0,
    };
  }
  const advancing = standings.filter((r) => r.rank < cutRank).map((r) => r.playerId);
  const tied = standings.filter((r) => r.rank === cutRank).map((r) => r.playerId);
  return { advancing, tied, needed: count - advancing.length };
};

// Final standings once the admin picked who wins a tie at the cut: picked
// players keep the shared rank, the rest drop behind them.
export const applyTieBreak = (standings, count, picked = []) => {
  const { advancing, tied, needed } = advancementCut(standings, count);
  if (!tied.length) return { standings, advancing };
  if (picked.length !== needed || picked.some((id) => !tied.includes(id))) {
    throw new Error(`Pick exactly ${needed} of the tied players.`);
  }
  const cutRank = standings.find((r) => r.playerId === tied[0]).rank;
  const next = standings
    .map((r) =>
      tied.includes(r.playerId) && !picked.includes(r.playerId)
        ? { ...r, rank: cutRank + needed }
        : r
    )
    .sort((a, b) => a.rank - b.rank);
  // Seed order = standings order, not the order the admin clicked.
  return { standings: next, advancing: next.slice(0, count).map((r) => r.playerId) };
};

// Standings of the last stage once it is completed, else null. Its first
// place is the tournament champion.
export const finalStandings = (stages) => {
  const last = [...stages].sort((a, b) => a.order - b.order)[stages.length - 1];
  if (!last || last.status !== 'completed') return null;
  return last.standings || [];
};

export const tournamentChampion = (stages) => finalStandings(stages)?.[0]?.playerId || null;

// The stage a viewer most likely wants: the running one, else the latest
// completed one, else the first.
export const focusStageId = (stages) => {
  const active = stages.find((s) => s.status === 'active');
  const done = [...stages].reverse().find((s) => s.status === 'completed');
  return (active || done || stages[0])?.id || null;
};

// ---- Lifecycle guards ---------------------------------------------------

// What the admin may do with stages[index] right now. Structure (add, remove,
// reorder) locks once any stage has started; settings lock per stage.
export const stageActions = (stages, index) => {
  const stage = stages[index];
  const prev = stages[index - 1];
  const next = stages[index + 1];
  const started = stages.some((s) => s.status !== 'pending');
  const invalid = stages.some((s, i) => validateStage(s, stages[i - 1]).length > 0);
  let startBlocked = null;
  if (stage.status !== 'pending') startBlocked = 'Already started.';
  else if (prev && prev.status !== 'completed') startBlocked = `Complete ${prev.name} first.`;
  else if (invalid) startBlocked = 'Fix the stage settings first.';
  else if (prev && (stage.participants || []).length < minParticipants(stage)) {
    startBlocked = `Needs at least ${minParticipants(stage)} players from ${prev.name}.`;
  }
  return {
    canChangeStructure: !started,
    canDelete: !started && stages.length > 1,
    canEdit: stage.status === 'pending',
    canEditAdvance: stage.status === 'pending' && !!prev && prev.status !== 'completed',
    canStart: !startBlocked,
    startBlocked,
    canComplete: stage.status === 'active',
    canReset: stage.status === 'active',
    canReopen: stage.status === 'completed' && (!next || next.status === 'pending'),
  };
};

export const stageSummary = (stage) => {
  if (stage.type === 'bracket') {
    return stage.elimination === 'double' ? 'Double elimination' : 'Single elimination';
  }
  const cats = stage.categories || [];
  return cats.length > 1 ? `Points · ${cats.length} categories` : 'Points';
};

// ---- Withdrawing players ------------------------------------------------

// Whether `playerId` can leave `stage` without breaking it. A running or
// finished bracket is built from its seed list, so taking someone out would
// reshuffle every match; leaderboards and unstarted stages are fine.
export const canWithdrawFrom = (stage, playerId) => {
  if (!(stage.participants || []).includes(playerId)) return true;
  return stage.type !== 'bracket' || stage.status === 'pending';
};

// The fields to write so `playerIds` no longer appear in `stage`: gone from
// the participants, their results dropped and, for a completed stage, the
// saved standings closed up (1, 2, 4 -> 1, 2, 3). null when nothing changes.
export const withoutParticipants = (stage, playerIds) => {
  const drop = new Set(playerIds);
  const participants = stage.participants || [];
  const standings = Array.isArray(stage.standings) ? stage.standings : null;
  const inStage = participants.some((id) => drop.has(id));
  const inStandings = !!standings && standings.some((r) => drop.has(r.playerId));
  if (!inStage && !inStandings) return null;

  const results = { ...(stage.results || {}) };
  playerIds.forEach((id) => delete results[id]);
  const patch = {
    participants: participants.filter((id) => !drop.has(id)),
    results,
  };
  if (standings) {
    const removed = standings.filter((r) => drop.has(r.playerId));
    patch.standings = standings
      .filter((r) => !drop.has(r.playerId))
      .map((r) => ({
        ...r,
        rank: r.rank - removed.filter((x) => x.rank < r.rank).length,
      }));
  }
  return patch;
};

// Participants whose roster entry no longer exists ("removed player").
export const orphanedParticipants = (stage, playersById) =>
  (stage.participants || []).filter((id) => !playersById[id]);

// ---- Guidance -------------------------------------------------------------

// One plain-language "what now" line for the admin, for stages[index].
// `ctx` = { rosterCount, votingOpen, averageVisible }.
export const nextStepHint = (stages, index, ctx = {}) => {
  const stage = stages[index];
  if (!stage) return null;
  const prev = stages[index - 1];
  const next = stages[index + 1];
  const { rosterCount = 0, votingOpen = true, averageVisible = false } = ctx;
  const voting = hasVoteCategory(stage);

  if (stage.status === 'pending') {
    if (prev && prev.status !== 'completed') {
      return {
        title: `Waiting for ${prev.name}`,
        body: `This stage fills itself with the top ${stage.advanceCount} of ${prev.name} once you complete it.`,
      };
    }
    if (!prev && rosterCount === 0) {
      return {
        title: 'Add players or entries first',
        body: 'Use the Roster section below to add who competes. Then come back and start this stage.',
      };
    }
    return {
      title: `Ready to start ${stage.name}`,
      body: `Press “Start stage” to pick who takes part${
        stage.type === 'bracket' ? ' and set the seeds' : ''
      }. Until then you can still edit the stage.${
        !prev && stage.type === 'leaderboard' ? ' Late entries can be added after it starts.' : ''
      }`,
    };
  }

  if (stage.status === 'active') {
    const finish = next
      ? `When you are done, press “Complete stage” to send the top ${next.advanceCount} to ${next.name}.`
      : 'When you are done, press “Complete stage” to crown the winner.';
    if (stage.type === 'bracket') {
      return {
        title: `${stage.name} is live`,
        body: `Click a match to enter its result. ${finish}`,
      };
    }
    if (voting) {
      const voteStep = votingOpen
        ? 'Community voting is open. When it should end, switch voting off, then switch the results to visible.'
        : !averageVisible
          ? 'Voting is closed. Switch the community results to visible to add them to the scores.'
          : 'Voting is closed and the community results are public.';
      return {
        title: `${stage.name} is live`,
        body: `Enter the judges’ scores below. ${voteStep} ${finish}`,
      };
    }
    return { title: `${stage.name} is live`, body: `Enter scores below. ${finish}` };
  }

  if (next && next.status === 'pending') {
    return {
      title: `${stage.name} is completed`,
      body: `Its top players are now in ${next.name}. Select ${next.name} above and start it. Made a mistake? “Reopen” undoes the completion.`,
    };
  }
  return {
    title: `${stage.name} is completed`,
    body: next
      ? 'Results are frozen.'
      : 'Results are frozen and the winner is shown on the public page.',
  };
};

// ---- Public sections ----------------------------------------------------

// Sections of a staged tournament's public page the admin can switch on or
// off one by one. Rules are always shown and have no switch.
export const PUBLIC_SECTIONS = [
  { key: 'stages', label: 'Stages', hint: 'Leaderboards, brackets and the champion.' },
  { key: 'players', label: 'Players', hint: 'The roster.' },
  { key: 'maps', label: 'Maps', hint: 'The map pool.' },
  { key: 'support', label: 'Prize & sponsors', hint: 'Prize pool and sponsor logos.' },
];

export const DEFAULT_VISIBILITY = { stages: false, players: false, maps: false, support: false };

export const isSectionVisible = (tournament, key) =>
  key === 'rules' || tournament?.visibility?.[key] === true;

// ---- Community vote -------------------------------------------------------

// Votes live in the legacy `communityVotes` docs (one per voter, id = uid,
// `votes: { [key]: rating }`). Staged keys are `${stageId}_${playerId}`.
export const voteKey = (stageId, playerId) => `${stageId}_${playerId}`;

// { [playerId]: { average, count } } for one stage. Ratings outside 1-10 or
// not whole numbers are ignored.
export const voteAverages = (voteDocs, stageId) => {
  const prefix = `${stageId}_`;
  const sums = {};
  (voteDocs || []).forEach((d) => {
    Object.entries(d.votes || {}).forEach(([key, rating]) => {
      if (!key.startsWith(prefix)) return;
      if (!Number.isInteger(rating) || rating < 1 || rating > 10) return;
      const playerId = key.slice(prefix.length);
      sums[playerId] = sums[playerId] || { sum: 0, count: 0 };
      sums[playerId].sum += rating;
      sums[playerId].count += 1;
    });
  });
  const out = {};
  Object.entries(sums).forEach(([playerId, { sum, count }]) => {
    out[playerId] = { average: sum / count, count };
  });
  return out;
};

// The stage with community averages written into its vote category, so the
// normal ranking functions can use it.
export const withCommunityVotes = (stage, averages) => {
  const category = (stage.categories || []).find(isVoteCategory);
  if (!category || !averages) return stage;
  const results = { ...(stage.results || {}) };
  Object.entries(averages).forEach(([playerId, { average, count }]) => {
    if (!count) return;
    results[playerId] = { ...(results[playerId] || {}), [category.id]: average };
  });
  return { ...stage, results };
};

// Rows to show for a leaderboard stage, best first. `stage` already carries
// any community averages (see withCommunityVotes). A completed stage keeps
// its saved standings; with the vote hidden, rows stay in seed order.
export const leaderboardRows = (stage, votesHidden = false) => {
  const rankedRows = rankLeaderboard(stage);
  const byPlayerId = Object.fromEntries(rankedRows.map((r) => [r.playerId, r]));
  if (votesHidden) return (stage.participants || []).map((id) => byPlayerId[id]);
  if (stage.status === 'completed' && Array.isArray(stage.standings)) {
    const standingIds = new Set(stage.standings.map((s) => s.playerId));
    const ordered = stage.standings.map((s) => ({ ...byPlayerId[s.playerId], rank: s.rank }));
    const others = rankedRows
      .filter((r) => !standingIds.has(r.playerId))
      .map((r) => ({ ...r, rank: null }));
    return [...ordered, ...others];
  }
  return rankedRows;
};

// ---- Legacy conversion ----------------------------------------------------

// Why a legacy tournament can't switch to stages. `found` = names of legacy
// data collections that hold at least one doc; `scoredPlayers` = players with
// points or map scores on their doc. [] = safe to switch.
export const conversionBlockers = ({ status, found = [], scoredPlayers = 0 }) => {
  const blockers = [];
  if (status === 'completed') blockers.push('The tournament has already ended.');
  if (scoredPlayers > 0) {
    blockers.push(`${scoredPlayers} player${scoredPlayers === 1 ? ' has' : 's have'} points.`);
  }
  if (found.includes('teams')) blockers.push('It has teams. Stages are for individual players.');
  const data = found.filter((name) => name !== 'teams');
  if (data.length) blockers.push(`It already has results or game data (${data.join(', ')}).`);
  return blockers;
};
